import { Navigate, useLocation } from "react-router-dom";
import { isPilotGatedProductPath, postAuthHome } from "@/app/pilotGate";
import { useAuth } from "@/features/auth/AuthProvider";
import { useBootstrap } from "@/features/bootstrap/BootstrapProvider";
import { safeNextPath } from "@/shared/lib/safeNextPath";
import { LoadingState } from "@/shared/ui/LoadingState";

export function RouteGuard({
  children,
  requiresAuth,
}: {
  children: JSX.Element;
  requiresAuth: boolean;
}) {
  const location = useLocation();
  const { session, loading } = useAuth();
  const { data } = useBootstrap();

  if (loading) {
    return <LoadingState label="Loading Liminl..." />;
  }

  if (requiresAuth && !session) {
    const next = safeNextPath(`${location.pathname}${location.search}`);
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(next)}`}
        replace
        state={{ from: location }}
      />
    );
  }

  const onboardingComplete = data?.profile?.onboardingStatus === "complete";
  const referencesSubmitted = Boolean(data?.profile?.hasSubmittedReferences);

  if (requiresAuth && session?.user.emailVerified === false) {
    const next = safeNextPath(
      `${location.pathname}${location.search}`,
      postAuthHome(onboardingComplete, referencesSubmitted),
    );
    return (
      <Navigate
        to={`/verify-email?email=${encodeURIComponent(session.user.email)}&next=${encodeURIComponent(next)}`}
        replace
        state={{ from: location }}
      />
    );
  }

  if (
    requiresAuth &&
    session &&
    data?.profile?.onboardingStatus &&
    !onboardingComplete &&
    location.pathname !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" replace state={{ from: location }} />;
  }

  if (
    requiresAuth &&
    session &&
    onboardingComplete &&
    !referencesSubmitted &&
    location.pathname !== "/references"
  ) {
    return <Navigate to="/references" replace state={{ from: location }} />;
  }

  if (
    requiresAuth &&
    session &&
    onboardingComplete &&
    referencesSubmitted &&
    (location.pathname === "/onboarding" ||
      location.pathname === "/references" ||
      isPilotGatedProductPath(location.pathname))
  ) {
    return <Navigate to={postAuthHome(true, true)} replace state={{ from: location }} />;
  }

  return children;
}
